import React, { useEffect, createElement } from 'react';
import { CheckIcon, XIcon, AlertCircleIcon, ZapIcon, ShieldCheckIcon, ClockIcon, MessageSquareIcon, BookOpenIcon, BrainIcon, ListChecksIcon } from 'lucide-react';
import { FAQAccordion } from '../components/FAQAccordion';
import { Footer } from '../components/Footer';
// Meta Pixel type declaration
declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
    _fbq: unknown;
  }
}
// Initialize Meta Pixel — uses document.head.appendChild to avoid parentNode crash
function initMetaPixel() {
  if (typeof window === 'undefined') return;
  if (window.fbq) return;
  const n: any = function (...args: unknown[]) {
    if (n.callMethod) {
      n.callMethod.apply(n, args);
    } else {
      n.queue.push(args);
    }
  };
  window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  window.fbq = n;
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
  window.fbq('init', '1656347615692560');
}
const faqItems = [{
  question: '¿Necesito saber mucho de tecnología?',
  answer: 'No, si sabes usar WhatsApp y copiar/pegar, puedes usar este kit. Está diseñado para ser simple y práctico desde el primer día.'
}, {
  question: '¿Funciona para limpieza comercial y residencial?',
  answer: 'Sí, los principios de psicología de ventas aplican para ambos tipos de servicio. Los guiones son adaptables a cualquier contexto.'
}, {
  question: '¿Tengo que comprar ChatGPT Plus?',
  answer: 'No, puedes usar la versión gratuita perfectamente con nuestros prompts. No necesitas gastar nada adicional.'
}, {
  question: '¿Qué pasa si mis precios sí son altos?',
  answer: 'Mejor aún. Este kit está diseñado para vender servicios de calidad, no para ser el más barato. Te ayudamos a justificar y defender tu precio con confianza.'
}];
export function LandingPage() {
  useEffect(() => {
    initMetaPixel();
    window.fbq('track', 'PageView');
  }, []);
  const handleBuyClick = () => {
    if (window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_name: 'The Clean & Close AI Kit',
        content_ids: ['52057323995428'],
        content_type: 'product',
        value: 27.0,
        currency: 'USD'
      });
    }
  };
  return <main className="w-full min-h-screen bg-white overflow-x-hidden" data-id="element-62">
      {/* ─── HERO ─────────────────────────────────────────────── */}
      <section className="bg-[#0f172a] text-white relative overflow-hidden" data-id="element-63">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-teal-600/20 blur-[100px] pointer-events-none" data-id="element-64" />

        <div className="relative max-w-5xl mx-auto px-5 py-16 md:py-24 flex flex-col items-center text-center" data-id="element-65">
          <div className="inline-flex items-center gap-2 bg-teal-600/20 border border-teal-500/40 text-teal-300 text-sm font-semibold px-4 py-1.5 rounded-full mb-8 tracking-wide uppercase" data-id="element-66">
            <ZapIcon className="w-3.5 h-3.5" data-id="element-67" />
            Kit Digital · Acceso Instantáneo
          </div>

          <h1 className="font-heading font-black text-4xl md:text-6xl lg:text-7xl leading-tight mb-6 max-w-4xl" data-id="element-68">
            El Kit <span className="text-teal-400" data-id="element-69">"Clean &amp; Close"</span> de
            IA para Negocios de Limpieza
          </h1>

          <p className="font-heading font-bold text-xl md:text-2xl text-amber-400 mb-8 max-w-3xl leading-snug" data-id="element-70">
            Deja de perder clientes por responder lento: El sistema de mensajes
            copiar/pegar que triplica tus reservas en USA sin bajar tus precios.
          </p>

          <div className="flex flex-col md:flex-row items-center gap-10 mt-4 w-full max-w-4xl" data-id="element-71">
            <div className="flex-shrink-0" data-id="element-72">
              <img src="https://cdn.magicpatterns.com/uploads/dZqj5hHXn5EKVuUtdqyybu/THE_CLEAN__CLOSE_AI_KIT_COVER.png" alt="The Clean & Close AI Kit — portada del producto" className="w-64 md:w-80 rounded-2xl shadow-2xl shadow-teal-900/40" data-id="element-73" />
            </div>

            <div className="flex flex-col items-center md:items-start gap-6 text-center md:text-left" data-id="element-74">
              <ul className="space-y-3 text-left" data-id="element-75">
                <li className="flex items-start gap-3 text-slate-200" data-id="element-76">
                  <CheckIcon className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" data-id="element-77" />
                  <span data-id="element-78">
                    Guiones listos para copiar y pegar en WhatsApp e Instagram
                  </span>
                </li>
                <li className="flex items-start gap-3 text-slate-200" data-id="element-79">
                  <CheckIcon className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" data-id="element-80" />
                  <span data-id="element-81">
                    20 prompts de ChatGPT para que la IA trabaje por ti 24/7
                  </span>
                </li>
                <li className="flex items-start gap-3 text-slate-200" data-id="element-82">
                  <CheckIcon className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" data-id="element-83" />
                  <span data-id="element-84">
                    Respuestas exactas para "está muy caro" y "lo voy a pensar"
                  </span>
                </li>
                <li className="flex items-start gap-3 text-slate-200" data-id="element-85">
                  <CheckIcon className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" data-id="element-86" />
                  <span data-id="element-87">Cierra clientes en menos de 5 minutos por chat</span>
                </li>
              </ul>

              <div className="flex flex-col items-center md:items-start gap-2" data-id="element-88">
                <div className="text-slate-400 text-sm line-through" data-id="element-89">
                  Valor real: $97 USD
                </div>
                <div className="text-white font-heading font-black text-5xl" data-id="element-90">
                  $27{' '}
                  <span className="text-2xl font-semibold text-slate-300" data-id="element-91">
                    USD
                  </span>
                </div>
                <div className="text-teal-300 text-sm font-medium" data-id="element-92">
                  Inversión única · Sin suscripción
                </div>
              </div>

              <a href="#comprar" className="cta-pulse inline-block bg-amber-400 hover:bg-amber-500 text-slate-900 font-heading font-black text-lg md:text-xl px-8 py-4 rounded-xl transition-colors duration-200 shadow-lg shadow-amber-500/30 w-full md:w-auto text-center" data-id="element-93">
                ¡QUIERO EL KIT AHORA!
              </a>

              <div className="flex items-center gap-2 text-slate-400 text-sm" data-id="element-94">
                <ShieldCheckIcon className="w-4 h-4 text-teal-400" data-id="element-95" />
                Pago 100% seguro · Acceso instantáneo tras la compra
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── OPENING HOOK ─────────────────────────────────────── */}
      <section className="bg-slate-50 py-16 md:py-20" data-id="element-96">
        <div className="max-w-3xl mx-auto px-5" data-id="element-97">
          <div className="bg-white border-l-4 border-teal-500 rounded-xl p-8 shadow-sm" data-id="element-98">
            <div className="flex items-center gap-3 mb-5" data-id="element-99">
              <ClockIcon className="w-6 h-6 text-teal-600 flex-shrink-0" data-id="element-100" />
              <span className="font-heading font-bold text-teal-700 text-sm uppercase tracking-wider" data-id="element-101">
                Una historia que conoces bien…
              </span>
            </div>
            <p className="text-slate-700 text-lg leading-relaxed mb-4" data-id="element-102">
              Son las <strong data-id="element-103">8:00 PM</strong>. Estás cansada después de un
              largo día de trabajo y recibes un mensaje:
            </p>
            <div className="bg-[#dcf8c6] rounded-2xl rounded-tl-none px-5 py-3 inline-block mb-4 shadow-sm max-w-xs" data-id="element-104">
              <p className="text-slate-800 text-base" data-id="element-105">
                "¿Hola, qué precio tienen por una limpieza?"
              </p>
            </div>
            <p className="text-slate-700 text-lg leading-relaxed mb-4" data-id="element-106">
              Respondes con tu tarifa y…{' '}
              <strong className="text-red-600" data-id="element-107">
                silencio total. Te dejaron en "visto".
              </strong>
            </p>
            <p className="text-slate-700 text-lg leading-relaxed" data-id="element-108">
              Ese silencio te está costando{' '}
              <strong data-id="element-109">miles de dólares al mes</strong> en gasolina, tiempo y
              facturas sin pagar. No es que tu precio sea caro — es que no
              tienes un <strong data-id="element-110">sistema de cierre profesional</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* ─── THE PROBLEM ──────────────────────────────────────── */}
      <section className="bg-white py-16 md:py-20" data-id="element-111">
        <div className="max-w-3xl mx-auto px-5" data-id="element-112">
          <div className="text-center mb-12" data-id="element-113">
            <span className="inline-block bg-red-100 text-red-700 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4" data-id="element-114">
              El Problema Real
            </span>
            <h2 className="font-heading font-black text-3xl md:text-4xl text-slate-900 leading-tight" data-id="element-115">
              Tu chat está trabajando{' '}
              <span className="text-red-600" data-id="element-116">en tu contra</span>
            </h2>
          </div>

          <div className="space-y-5" data-id="element-117">
            <div className="flex items-start gap-4 bg-red-50 border border-red-100 rounded-xl p-5" data-id="element-118">
              <AlertCircleIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-119" />
              <div data-id="element-120">
                <p className="font-heading font-bold text-slate-800 mb-1" data-id="element-121">
                  Compites solo por precio
                </p>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-122">
                  Cuando respondes solo con un número, te conviertes en una
                  opción barata más en la lista del cliente.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-red-50 border border-red-100 rounded-xl p-5" data-id="element-123">
              <AlertCircleIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-124" />
              <div data-id="element-125">
                <p className="font-heading font-bold text-slate-800 mb-1" data-id="element-126">
                  Tardas más de 15 minutos en contestar
                </p>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-127">
                  El cliente ya llamó a alguien más. La velocidad de respuesta
                  es tu ventaja competitiva más poderosa.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-red-50 border border-red-100 rounded-xl p-5" data-id="element-128">
              <AlertCircleIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-129" />
              <div data-id="element-130">
                <p className="font-heading font-bold text-slate-800 mb-1" data-id="element-131">
                  No sabes qué decir cuando te dicen "está muy caro"
                </p>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-132">
                  Pierdes el trabajo. Estás trabajando duro físicamente, pero tu
                  chat está trabajando en tu contra.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── WHY WHAT YOU TRIED DIDN'T WORK ──────────────────── */}
      <section className="bg-slate-900 text-white py-16 md:py-20" data-id="element-133">
        <div className="max-w-3xl mx-auto px-5" data-id="element-134">
          <div className="text-center mb-12" data-id="element-135">
            <span className="inline-block bg-slate-700 text-slate-300 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4" data-id="element-136">
              Por qué no ha funcionado
            </span>
            <h2 className="font-heading font-black text-3xl md:text-4xl leading-tight" data-id="element-137">
              Lo que has intentado{' '}
              <span className="text-amber-400" data-id="element-138">te está saboteando</span>
            </h2>
          </div>
          <div className="space-y-4" data-id="element-139">
            {['Dar el precio de inmediato mata la curiosidad y el valor de tu servicio.', 'Enviar párrafos gigantes de texto abruma al cliente y hace que te ignore.', 'Responder sin una pregunta al final entrega el control de la venta al prospecto.', 'No tener fotos de antes/después listas para enviar reduce la confianza al instante.'].map((text, i) => <div key={i} className="flex items-start gap-4 border border-slate-700 rounded-xl p-5" data-id="element-140">
                <XIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-141" />
                <p className="text-slate-300 leading-relaxed" data-id="element-142">{text}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* ─── UNIQUE MECHANISM ─────────────────────────────────── */}
      <section className="bg-teal-600 text-white py-16 md:py-20" data-id="element-143">
        <div className="max-w-3xl mx-auto px-5 text-center" data-id="element-144">
          <span className="inline-block bg-teal-500/50 border border-teal-400/40 text-teal-100 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6" data-id="element-145">
            La Solución
          </span>
          <h2 className="font-heading font-black text-3xl md:text-5xl leading-tight mb-6" data-id="element-146">
            The Ghost-Proof Script
            <br data-id="element-147" />
            <span className="text-amber-300" data-id="element-148">El Guión Anti-Visto</span>
          </h2>
          <p className="text-teal-100 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-8" data-id="element-149">
            Un método de comunicación táctica diseñado para el mercado hispano
            en USA que utiliza la{' '}
            <strong className="text-white" data-id="element-150">
              psicología de la escasez y la validación
            </strong>
            .
          </p>
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 text-left" data-id="element-151">
            <p className="text-white text-lg leading-relaxed" data-id="element-152">
              En lugar de dar un precio, abres un proceso de{' '}
              <strong className="text-amber-300" data-id="element-153">"asesoría relámpago"</strong>{' '}
              que obliga al cliente a interactuar contigo, elevando tu autoridad
              y permitiéndote cobrar lo que realmente vales.
            </p>
          </div>
        </div>
      </section>

      {/* ─── WHAT YOU GET ─────────────────────────────────────── */}
      <section className="bg-white py-16 md:py-20" data-id="element-154">
        <div className="max-w-4xl mx-auto px-5" data-id="element-155">
          <div className="text-center mb-12" data-id="element-156">
            <span className="inline-block bg-teal-100 text-teal-700 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4" data-id="element-157">
              Lo que incluye
            </span>
            <h2 className="font-heading font-black text-3xl md:text-4xl text-slate-900 leading-tight" data-id="element-158">
              Todo lo que obtienes por{' '}
              <span className="text-teal-600" data-id="element-159">$27 USD</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-160">
            <div className="border-2 border-teal-100 rounded-2xl p-6 hover:border-teal-400 transition-colors duration-200" data-id="element-161">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4" data-id="element-162">
                <BookOpenIcon className="w-6 h-6 text-teal-600" data-id="element-163" />
              </div>
              <h3 className="font-heading font-bold text-xl text-slate-900 mb-2" data-id="element-164">
                El Manual Maestro (PDF)
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-165">
                Estrategias de psicología de ventas diseñadas específicamente
                para dueñas de negocio de limpieza en USA.
              </p>
            </div>

            <div className="border-2 border-teal-100 rounded-2xl p-6 hover:border-teal-400 transition-colors duration-200" data-id="element-166">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4" data-id="element-167">
                <MessageSquareIcon className="w-6 h-6 text-teal-600" data-id="element-168" />
              </div>
              <h3 className="font-heading font-bold text-xl text-slate-900 mb-2" data-id="element-169">
                Guiones "Clean &amp; Close"
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-170">
                Mensajes listos para copiar y pegar en WhatsApp, DM e Instagram.
                Sin improvisar, sin perder ventas.
              </p>
            </div>

            <div className="border-2 border-teal-100 rounded-2xl p-6 hover:border-teal-400 transition-colors duration-200" data-id="element-171">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4" data-id="element-172">
                <ZapIcon className="w-6 h-6 text-teal-600" data-id="element-173" />
              </div>
              <h3 className="font-heading font-bold text-xl text-slate-900 mb-2" data-id="element-174">
                Domador de Objeciones
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-175">
                Qué responder exactamente al "Lo voy a pensar" o "Está muy
                caro". Nunca más te quedarás sin palabras.
              </p>
            </div>

            <div className="border-2 border-teal-100 rounded-2xl p-6 hover:border-teal-400 transition-colors duration-200" data-id="element-176">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4" data-id="element-177">
                <BrainIcon className="w-6 h-6 text-teal-600" data-id="element-178" />
              </div>
              <h3 className="font-heading font-bold text-xl text-slate-900 mb-2" data-id="element-179">
                Bóveda de Prompts de IA
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-180">
                20 comandos maestros de ChatGPT para que la IA sea tu secretaria
                24/7. Funciona con la versión gratuita.
              </p>
            </div>

            <div className="border-2 border-amber-200 bg-amber-50 rounded-2xl p-6 md:col-span-2" data-id="element-181">
              <div className="flex items-start gap-4" data-id="element-182">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-183">
                  <ListChecksIcon className="w-6 h-6 text-amber-600" data-id="element-184" />
                </div>
                <div data-id="element-185">
                  <div className="inline-block bg-amber-200 text-amber-800 text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded mb-2" data-id="element-186">
                    Bono Extra
                  </div>
                  <h3 className="font-heading font-bold text-xl text-slate-900 mb-2" data-id="element-187">
                    Checklist de Perfil de Confianza
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-188">
                    Cómo configurar tu perfil para que los clientes te elijan a
                    ti antes que a la competencia.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── WHO IT'S FOR / NOT FOR ───────────────────────────── */}
      <section className="bg-slate-50 py-16 md:py-20" data-id="element-189">
        <div className="max-w-4xl mx-auto px-5" data-id="element-190">
          <div className="text-center mb-12" data-id="element-191">
            <h2 className="font-heading font-black text-3xl md:text-4xl text-slate-900 leading-tight" data-id="element-192">
              ¿Es este kit para ti?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-193">
            <div className="bg-white rounded-2xl p-8 border-2 border-teal-200 shadow-sm" data-id="element-194">
              <div className="flex items-center gap-3 mb-6" data-id="element-195">
                <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center" data-id="element-196">
                  <CheckIcon className="w-5 h-5 text-teal-600" data-id="element-197" />
                </div>
                <h3 className="font-heading font-black text-xl text-teal-700" data-id="element-198">
                  SÍ es para ti si…
                </h3>
              </div>
              <ul className="space-y-4" data-id="element-199">
                {['Eres dueña de una compañía de limpieza en USA y quieres profesionalizarte.', 'Limpias sola y quieres empezar a contratar personal.', 'Recibes mensajes de interesados pero no logras cerrar las fechas.', 'Sientes que la competencia "está regalando el trabajo" y quieres diferenciarte.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-200">
                    <CheckIcon className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" data-id="element-201" />
                    <span className="text-slate-700" data-id="element-202">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 border-2 border-slate-200 shadow-sm" data-id="element-203">
              <div className="flex items-center gap-3 mb-6" data-id="element-204">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center" data-id="element-205">
                  <XIcon className="w-5 h-5 text-red-500" data-id="element-206" />
                </div>
                <h3 className="font-heading font-black text-xl text-slate-500" data-id="element-207">
                  NO es para ti si…
                </h3>
              </div>
              <ul className="space-y-4" data-id="element-208">
                {['Buscas empleo de limpieza (este es un kit para dueñas de negocio).', 'No estás dispuesta a usar WhatsApp o redes sociales para vender.', 'No te importa perder clientes por mal servicio al cliente.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-209">
                    <XIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-210" />
                    <span className="text-slate-500" data-id="element-211">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─────────────────────────────────────── */}
      <section className="bg-white py-16 md:py-20" data-id="element-212">
        <div className="max-w-3xl mx-auto px-5" data-id="element-213">
          <div className="text-center mb-12" data-id="element-214">
            <span className="inline-block bg-teal-100 text-teal-700 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4" data-id="element-215">
              Así de simple
            </span>
            <h2 className="font-heading font-black text-3xl md:text-4xl text-slate-900 leading-tight" data-id="element-216">
              Empieza a cerrar clientes{' '}
              <span className="text-teal-600" data-id="element-217">hoy mismo</span>
            </h2>
          </div>

          <div className="space-y-6" data-id="element-218">
            <div className="flex items-start gap-6" data-id="element-219">
              <div className="w-12 h-12 bg-teal-600 text-white font-heading font-black text-xl rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-teal-200" data-id="element-220">
                1
              </div>
              <div className="pt-2" data-id="element-221">
                <h3 className="font-heading font-bold text-xl text-slate-900 mb-1" data-id="element-222">
                  Descarga el kit
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-223">
                  Copia los guiones maestros en las notas de tu celular. Listo
                  en 10 minutos.
                </p>
              </div>
            </div>
            <div className="ml-6 border-l-2 border-dashed border-teal-200 h-8" data-id="element-224" />
            <div className="flex items-start gap-6" data-id="element-225">
              <div className="w-12 h-12 bg-teal-600 text-white font-heading font-black text-xl rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-teal-200" data-id="element-226">
                2
              </div>
              <div className="pt-2" data-id="element-227">
                <h3 className="font-heading font-bold text-xl text-slate-900 mb-1" data-id="element-228">
                  Usa el Ghost-Proof Script
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-229">
                  La próxima vez que un cliente pregunte "Precio?", aplicas el
                  guión. Sin improvisar.
                </p>
              </div>
            </div>
            <div className="ml-6 border-l-2 border-dashed border-teal-200 h-8" data-id="element-230" />
            <div className="flex items-start gap-6" data-id="element-231">
              <div className="w-12 h-12 bg-amber-400 text-slate-900 font-heading font-black text-xl rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-200" data-id="element-232">
                3
              </div>
              <div className="pt-2" data-id="element-233">
                <h3 className="font-heading font-bold text-xl text-slate-900 mb-1" data-id="element-234">
                  Agenda la visita en menos de 5 minutos
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-235">
                  Sigues el proceso de cierre y conviertes el mensaje en dinero
                  real.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PROOF ────────────────────────────────────────────── */}
      <section className="bg-teal-700 text-white py-16 md:py-20" data-id="element-236">
        <div className="max-w-3xl mx-auto px-5 text-center" data-id="element-237">
          <span className="inline-block bg-teal-600/50 border border-teal-400/40 text-teal-200 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6" data-id="element-238">
            Lo que cambia en 7 días
          </span>
          <h2 className="font-heading font-black text-3xl md:text-4xl leading-tight mb-8" data-id="element-239">
            De la ansiedad a la{' '}
            <span className="text-amber-300" data-id="element-240">agenda llena</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left" data-id="element-241">
            <div className="bg-white/10 border border-white/20 rounded-2xl p-6" data-id="element-242">
              <div className="text-red-300 font-heading font-bold text-sm uppercase tracking-wide mb-3" data-id="element-243">
                Antes del kit
              </div>
              <p className="text-teal-100 leading-relaxed" data-id="element-244">
                Sientes ansiedad cada vez que suena el teléfono. Tus chats son
                cementerios de mensajes en "visto".
              </p>
            </div>
            <div className="bg-white/10 border border-amber-400/30 rounded-2xl p-6" data-id="element-245">
              <div className="text-amber-300 font-heading font-bold text-sm uppercase tracking-wide mb-3" data-id="element-246">
                Después del kit
              </div>
              <p className="text-white leading-relaxed" data-id="element-247">
                Tienes la seguridad de que cada mensaje es una oportunidad real
                de dinero. Tu agenda está llena.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FAQ ──────────────────────────────────────────────── */}
      <section className="bg-slate-50 py-16 md:py-20" data-id="element-248">
        <div className="max-w-3xl mx-auto px-5" data-id="element-249">
          <div className="text-center mb-12" data-id="element-250">
            <span className="inline-block bg-slate-200 text-slate-600 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4" data-id="element-251">
              Preguntas frecuentes
            </span>
            <h2 className="font-heading font-black text-3xl md:text-4xl text-slate-900 leading-tight" data-id="element-252">
              Resolvemos tus dudas
            </h2>
          </div>
          <FAQAccordion items={faqItems} data-id="element-253" />
        </div>
      </section>

      {/* ─── OFFER + CTA ──────────────────────────────────────── */}
      <section id="comprar" className="bg-[#0f172a] text-white py-16 md:py-24" data-id="element-254">
        <div className="max-w-2xl mx-auto px-5 text-center" data-id="element-255">
          <span className="inline-block bg-amber-400/20 border border-amber-400/40 text-amber-300 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6" data-id="element-256">
            Oferta especial
          </span>

          <h2 className="font-heading font-black text-3xl md:text-5xl leading-tight mb-6" data-id="element-257">
            Obtén el sistema completo
            <br data-id="element-258" />
            <span className="text-teal-400" data-id="element-259">
              The "Clean &amp; Close" AI Kit
            </span>
          </h2>

          <p className="text-slate-300 text-lg mb-8 leading-relaxed" data-id="element-260">
            Empieza a cerrar clientes de forma profesional y recupera el control
            de tu negocio hoy mismo.
          </p>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 mb-8" data-id="element-261">
            <div className="text-slate-400 text-base line-through mb-1" data-id="element-262">
              Valor real: $97 USD
            </div>
            <div className="font-heading font-black text-6xl text-white mb-2" data-id="element-263">
              $27{' '}
              <span className="text-2xl font-semibold text-slate-400" data-id="element-264">USD</span>
            </div>
            <div className="text-teal-400 font-medium mb-6" data-id="element-265">
              Inversión única · Sin suscripción mensual
            </div>
            <p className="text-amber-300 text-sm italic" data-id="element-266">
              "Menos de lo que ganas en media hora de limpieza"
            </p>
          </div>

          <a href="https://www.mdbhorizonte.com/cart/52057323995428:1" target="_blank" rel="noopener noreferrer" onClick={handleBuyClick} className="cta-pulse inline-block bg-amber-400 hover:bg-amber-500 text-slate-900 font-heading font-black text-xl md:text-2xl px-10 py-5 rounded-xl transition-colors duration-200 shadow-xl shadow-amber-500/30 w-full mb-6" data-id="element-267">
            ¡QUIERO EL KIT Y CERRAR MÁS CLIENTES!
          </a>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-slate-400 text-sm" data-id="element-268">
            <div className="flex items-center gap-2" data-id="element-269">
              <ShieldCheckIcon className="w-4 h-4 text-teal-400" data-id="element-270" />
              Pago 100% seguro
            </div>
            <div className="hidden sm:block w-1 h-1 bg-slate-600 rounded-full" data-id="element-271" />
            <div className="flex items-center gap-2" data-id="element-272">
              <ZapIcon className="w-4 h-4 text-teal-400" data-id="element-273" />
              Acceso instantáneo tras la compra
            </div>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ───────────────────────────────────────────── */}
      <Footer data-id="element-274" />
    </main>;
}